import React from "react";
import styled from "styled-components";
import tw from "twin.macro";
import { useTranslation } from "react-i18next";
import nccBuildingImg from "../../images/GroupCompany/ncc-building-motoasakusa.jpeg";
import {
  Container as ContainerBase,
  ContentWithPaddingXl,
} from "components/misc/Layouts";
import BgTitle from "components/title/BgTitle";

const Container = styled(ContainerBase)`
  ${tw`relative`};
`;

const BackgroundImageContainer = styled.div`
  ${tw`absolute inset-0 bg-cover bg-center`};
  background-image: url(${nccBuildingImg});
  opacity: 0.2;
  border-radius: 12px;

  @media (max-width: 640px) {
    margin: 0; /* Remove margin on small screens */
    border-radius: 0;
  }
`;

const Overlay = styled.div`
  ${tw`absolute inset-0 bg-[#1565c07d] opacity-50`};
  border-radius: 12px;

  @media (max-width: 640px) {
    margin: 0;
    border-radius: 0;
  }
`;

const FourColumnGrid = styled.div`
  ${tw`relative grid gap-6 mt-4 max-w-screen-xl mx-auto`};

  grid-template-columns: 1fr;

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr; /* Two columns on small screens */
  }

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr; /* Three columns on medium screens */
  }

  @media (min-width: 1280px) {
    grid-template-columns: 1fr 1fr 1fr 1fr; /* Four columns on large screens */
  }
`;

const HistoryCard = styled.div`
  ${tw`relative p-4 sm:p-6 rounded-lg bg-white shadow-md`};
  border-left: 2px solid #42a5f5;
  font-size: 0.9rem;
  color: #333;
  transition: all 0.3s ease;

  &:hover {
    ${tw`shadow-lg`};
    transform: translateY(-4px);
    border-left: 4px solid #42a5f5;
    background-color: #f0f4f8;
  }
`;

const HistoryCardText = styled.div`
  ${tw`text-sm font-medium`};
  color: #555;
`;

const CardYear = styled.strong`
  ${tw`text-sm font-semibold text-primary-500 block`};
  color: #1565c0;
  margin-bottom: 8px;
`;

const DecadeText = styled.strong`
  ${tw`text-lg font-bold text-ncc block my-4 sm:my-6 text-center sm:text-left`}
`;

const SectionTitle = styled(BgTitle)`
  ${tw`text-center mb-4`}
`;

const SectionDivider = styled.hr`
  ${tw`my-6 sm:my-8 border-t border-gray-200`};
  opacity: 0.6;
`;

export default () => {
  const { t } = useTranslation();

  // History data fetched from translations
  const companyHistory = t("company_history.events", { returnObjects: true });

  // Grouped history by decades
  const groupedHistory = companyHistory.reduce((acc, item) => {
    const decade = Math.floor(parseInt(item.year) / 10) * 10 + "s";
    if (!acc[decade]) acc[decade] = [];
    acc[decade].push(item);
    return acc;
  }, {});

  return (
    <Container>
      <BackgroundImageContainer />
      <Overlay />
      <ContentWithPaddingXl>
        <SectionTitle text={t("company_history.title")} />
        <div style={{ marginTop: "40px" }}>
          {Object.keys(groupedHistory).map((decade, idx) => (
            <React.Fragment key={idx}>
              <DecadeText>{decade}</DecadeText>
              <FourColumnGrid>
                {groupedHistory[decade].map((item, idx) => (
                  <HistoryCard key={idx}>
                    <CardYear>{item.year}</CardYear>
                    <HistoryCardText>{item.event}</HistoryCardText>
                  </HistoryCard>
                ))}
              </FourColumnGrid>
              {idx < Object.keys(groupedHistory).length - 1 && (
                <SectionDivider />
              )}
            </React.Fragment>
          ))}
        </div>
      </ContentWithPaddingXl>
    </Container>
  );
};
